import React from 'react'
import { Separator } from '@components/ui/separator'
import { cn } from '@/utils'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  type PhotoActionHandlers,
  PhotoActionsList,
} from '@/components/app/photos/photo-actions-list'
import {
  CLIKART_ENABLED_PHOTO_ACTION_IDS,
  CLIKART_PHOTO_ACTION_DEFINITIONS,
  type PhotoActionId,
} from '@/lib/photos/photo-action-items'
import { useClikArtPanelContext } from './hooks/use-clikart-panel-context'

interface ClikArtPanelProps {
  className?: string
  interactionLocked?: boolean
  selectedClikArtShape?: ShapeData
  onCropClikArt?: () => void
  onClippingPath?: () => void
  onRemoveClikArt?: () => void
}

interface InfoRowProps {
  label: string
  value: string
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
  <div className="flex gap-1 items-start w-full">
    <div className="w-[100px] shrink-0 text-sm font-normal text-muted-foreground">{label}</div>
    <div className="flex-1 min-w-0 text-sm font-normal text-foreground text-right">{value}</div>
  </div>
)

/**
 * The Select tool's section for one selected placed ClikArt (WMPCM-11032, WMPCM-12938): its details
 * and the actions that apply to it. Only four are shown (`CLIKART_PHOTO_ACTION_DEFINITIONS`: Edit,
 * Crop, Clipping path, Remove); the photo-only ones (effects, tagging, background removal, caption)
 * need a candid `imageId`, which ClikArt lacks, and Photo effects would misread the ClikArt's
 * `pezId` (its art id) as a PEZ effects id, so they are hidden, not disabled. Edit ClikArt has no
 * handler yet, same as Edit photo for a photo (`PHOTO_PLACEHOLDER_ACTION_IDS`).
 */
export const ClikArtPanel: React.FC<ClikArtPanelProps> = ({
  className,
  interactionLocked = false,
  selectedClikArtShape,
  onCropClikArt,
  onClippingPath,
  onRemoveClikArt,
}) => {
  const { fileNameLabel, categoryLabel, dimensionsLabel, placedLabel } =
    useClikArtPanelContext(selectedClikArtShape)

  const actionHandlers = React.useMemo<PhotoActionHandlers>(
    () => ({
      cropPhoto: onCropClikArt,
      clippingPath: onClippingPath,
      removePhoto: onRemoveClikArt,
    }),
    [onCropClikArt, onClippingPath, onRemoveClikArt]
  )

  const isActionDisabled = React.useCallback(
    (id: PhotoActionId) =>
      interactionLocked || !selectedClikArtShape || !CLIKART_ENABLED_PHOTO_ACTION_IDS.has(id),
    [interactionLocked, selectedClikArtShape]
  )

  return (
    <div
      className={cn(
        'flex flex-col gap-4',
        className,
        interactionLocked && 'pointer-events-none select-none opacity-60'
      )}
      aria-disabled={interactionLocked || undefined}
    >
      <p className="text-sm font-medium text-foreground leading-5">General Info</p>

      <div className="flex flex-col gap-2">
        <InfoRow label="File name" value={fileNameLabel} />
        <InfoRow label="Category" value={categoryLabel} />
        <InfoRow label="Dimensions" value={dimensionsLabel} />
        <InfoRow label="Placed" value={placedLabel} />
      </div>

      <Separator />

      <p className="text-sm font-medium text-foreground leading-5">ClikArt Actions</p>

      <PhotoActionsList
        actions={CLIKART_PHOTO_ACTION_DEFINITIONS}
        handlers={actionHandlers}
        isDisabled={isActionDisabled}
      />
    </div>
  )
}
